// PlayFirst Parent — onboarding wizard. Steps 2–10 of the brief, mapped to
// a single multi-step form. Step 1 (welcome) is the /parent-app landing page
// before signup. On final submit, the wizard POSTs to /api/parent-app/children
// then immediately POSTs /language-map and /play-idea to seed the Home page.
//
// Design intent: one card on screen at a time, soft progress bar, friendly
// microcopy, never any "milestone" or pass/fail language.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Loader2, Heart, Sparkles } from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const REASONS = [
  'I want to support my child\'s development',
  'I want to understand my child\'s communication better',
  'I am wondering if my child is on track',
  'My child has delayed speech or language',
  'My child is autistic or neurodivergent',
  'My child is a gestalt language processor / GLP',
  'My child uses AAC, signs or gestures',
  'My child is already in therapy',
  'I am not sure',
];

const DIAGNOSIS = [
  'No diagnosis or profile',
  'Autism / autistic',
  'ADHD',
  'Developmental delay',
  'Speech sound delay',
  'Language delay',
  'Global developmental delay',
  'Down syndrome',
  'Cerebral palsy',
  'Hearing loss',
  'Childhood apraxia of speech',
  'Intellectual disability',
  'Gestalt language processor / GLP',
  'Sensory processing differences',
  'AAC user',
  'Other',
  'Prefer not to say',
];

const COMMUNICATION_STYLE = [
  'Looks, facial expressions or body movements',
  'Reaches, pulls, gives or shows objects',
  'Points or gestures',
  'Uses sounds or vocal play',
  'Babbles',
  'Uses a few single words',
  'Uses many single words',
  'Uses short phrases',
  'Uses sentences',
  'Uses repeated phrases or scripts',
  'Uses songs, movie lines or phrases from shows',
  'Uses signs',
  'Uses pictures or AAC',
  'Uses behaviour to show what they need',
  'I am not sure',
];

const UNDERSTANDING = [
  'Familiar routines',
  'Names of favourite people or objects',
  'Simple choices',
  'Simple everyday instructions',
  'Instructions with two parts',
  'Questions like "where?" or "what?"',
  'Stories or explanations',
  'My child understands more than they can say',
  'I am not sure',
];

const SCRIPT_USE = [
  { v: 'yes_often', l: 'Yes, often' },
  { v: 'sometimes', l: 'Sometimes' },
  { v: 'no', l: 'No' },
  { v: 'not_sure', l: 'I am not sure' },
];

const GLP_PREFERENCE = [
  { v: 'yes', l: 'Yes, my child is a GLP' },
  { v: 'maybe', l: 'Maybe, I am learning about GLP' },
  { v: 'not_sure', l: 'Not sure' },
  { v: 'no', l: 'No, not relevant' },
];

const GLP_DESCRIPTOR = [
  'Uses whole phrases or scripts in specific situations',
  'Uses scripts that seem meaningful, but I am not always sure what they mean',
  'Mixes parts of scripts together',
  'Uses short flexible phrases from scripts',
  'Uses more original/self-made sentences now',
  'I am not sure',
];

const INTERESTS = [
  'Vehicles', 'Animals', 'Dolls/teddies', 'Bubbles', 'Water play',
  'Music/songs', 'Books', 'Sensory play', 'Movement/climbing',
  'Pretend cooking', 'Blocks', 'Letters/numbers',
  'Favourite shows or characters', 'Snacks', 'Outdoor play',
  'Spinning/wheels/lights', 'Drawing/crafts', 'Other',
];

const PLAY_STYLE = [
  'Explores objects',
  'Lines up or organises things',
  'Watches parts of toys, like wheels or lights',
  'Repeats favourite actions',
  'Plays simple cause-and-effect games',
  'Pretends with toys',
  'Plays alongside others',
  'Plays back-and-forth with others',
  'Enjoys physical play',
  'I am not sure',
];

const REGULATION_SUPPORTS = [
  'Quiet space', 'Movement', 'Deep pressure', 'Predictable routines',
  'Music', 'Visual support', 'Extra time', 'Favourite toys',
  'Being outside', 'Fewer questions', 'Choices', 'Sensory play',
  'AAC/signs/pictures', 'I am not sure',
];

const COMMUNICATION_BARRIERS = [
  'Too many questions', 'Pressure to talk', 'Noise', 'Transitions',
  'Tiredness', 'Hunger', 'Busy places', 'New people', 'Waiting',
  'Frustration', 'Sensory overload', 'I am not sure',
];

const PARENT_GOALS = [
  'Understanding what my child is trying to communicate',
  'Helping my child communicate wants and needs',
  'Supporting first words',
  'Supporting scripts, echolalia or GLP',
  'Building back-and-forth interaction',
  'Supporting play',
  'Reducing frustration around communication',
  'Helping my child understand routines',
  'Supporting speech clarity',
  'Supporting AAC/signs/gestures',
  'Tracking development gently',
  'I am not sure',
];

const TOTAL_STEPS = 11;

const FREQS = [
  { v: 'daily', l: 'Daily', sub: 'One small idea every day' },
  { v: 'weekly', l: 'Weekly', sub: 'A gentle ping once a week' },
  { v: 'biweekly', l: 'Every 2 weeks', sub: 'Less often, still consistent' },
  { v: 'monthly', l: 'Monthly', sub: 'Just a soft check-in once a month' },
  { v: 'never', l: 'Never', sub: 'No outbound messages — you reach out to me' },
];

// Friendly "X years Y months" formatter for the age preview.
function formatAgeReadable(yearsRaw, monthsRaw) {
  const y = parseInt(yearsRaw, 10);
  const m = parseInt(monthsRaw, 10);
  const safeY = Number.isFinite(y) ? y : 0;
  const safeM = Number.isFinite(m) ? Math.min(11, Math.max(0, m)) : 0;
  if (safeY === 0 && safeM === 0) return '';
  if (safeY === 0) return `${safeM} month${safeM === 1 ? '' : 's'}`;
  if (safeM === 0) return `${safeY} year${safeY === 1 ? '' : 's'}`;
  return `${safeY} year${safeY === 1 ? '' : 's'} ${safeM} month${safeM === 1 ? '' : 's'}`;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${PFP.chip} ${active ? PFP.chipActive : PFP.chipIdle}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function ProgressBar({ step }) {
  const pct = Math.round((step / TOTAL_STEPS) * 100);
  return (
    <div className="w-full h-1.5 bg-[#7C6F9C]/10 rounded-full overflow-hidden mb-8">
      <motion.div
        className="h-full bg-[#7C6F9C]"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
}

export default function ParentAppOnboarding() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  // Allow re-entry to onboard additional siblings — guarded by ?add=1 from the home page.
  const isAddingAnother = typeof window !== 'undefined'
    && window.location.search.includes('add=1');
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    age_years: '',
    age_years_input: '',   // UI-only — integer years
    age_months_input: '',  // UI-only — integer 0-11
    languages_at_home: [],
    country: '',
    whatsapp_number: '',
    reasons: [],
    diagnosis_profile: [],
    communication_style: [],
    understanding: [],
    uses_scripts: '',
    glp_preference: '',
    glp_stage_descriptor: '',
    interests: [],
    play_style: [],
    regulation_supports: [],
    communication_barriers: [],
    parent_goals: [],
    whatsapp_reminder_frequency: 'weekly',
  });

  const toggle = (key, val) => {
    setForm((f) => {
      const arr = f[key] || [];
      const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
      return { ...f, [key]: next };
    });
  };
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const canNext = () => {
    switch (step) {
      case 1:
        return form.name.trim().length > 0;
      case 4:
        return form.communication_style.length > 0;
      default:
        return true;
    }
  };

  const next = () => {
    if (!canNext()) {
      toast.error('Please make a selection to continue');
      return;
    }
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else handleSubmit();
  };
  const back = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      // Compute decimal age_years from the years + months UI inputs.
      const yi = parseInt(form.age_years_input, 10);
      const mi = parseInt(form.age_months_input, 10);
      const yearsPart = Number.isFinite(yi) ? yi : 0;
      const monthsPart = Number.isFinite(mi) ? Math.min(11, Math.max(0, mi)) : 0;
      const computedAge = yearsPart + monthsPart / 12;
      // Best-effort DOB from years+months (today minus age). The parent can
      // override the precise day later on Settings → this lets the app
      // age-up automatically over time WITHOUT requiring an exact birthday.
      let computedDob = null;
      if (yearsPart > 0 || monthsPart > 0) {
        const totalMonths = yearsPart * 12 + monthsPart;
        const d = new Date();
        d.setMonth(d.getMonth() - totalMonths);
        // Format YYYY-MM-DD
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        computedDob = `${yyyy}-${mm}-${dd}`;
      }
      const payload = {
        ...form,
        age_years: computedAge > 0 ? Number(computedAge.toFixed(2)) : null,
        date_of_birth: computedDob,
      };
      // Strip UI-only fields so backend pydantic doesn't reject them.
      delete payload.age_years_input;
      delete payload.age_months_input;
      const childRes = await axios.post(`${API}/parent-app/children`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const childId = childRes.data.id;

      // Fire-and-await both seeds before redirecting so Home renders instantly.
      // We swallow errors here — Home will retry if either is missing.
      try {
        await axios.post(
          `${API}/parent-app/children/${childId}/language-map`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        /* Home will retry */
      }
      try {
        await axios.post(
          `${API}/parent-app/children/${childId}/play-idea`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        /* Home will retry */
      }

      localStorage.setItem('parent_app_child_id', childId);
      // Make sure the new child is the active one on Parent Home when we return.
      try { localStorage.setItem('parentapp_selected_child_id', childId); } catch {}

      // Persist the chosen WhatsApp reminder frequency. Best-effort.
      if (form.whatsapp_reminder_frequency) {
        try {
          await axios.put(
            `${API}/parent-app/whatsapp/preferences`,
            { reminder_frequency: form.whatsapp_reminder_frequency },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (e) { /* non-blocking */ }
      }

      toast.success(`${form.name}'s Language Map is ready`);
      navigate('/parent-app/home');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setBusy(false);
    }
  };

  // -------- step renderers --------
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step
            title={isAddingAnother ? "Tell us about your other little one" : "Tell us a little about your little one"}
            subtitle="From newborn onwards. Age is just a guide — how your child communicates today matters most."
          >
            <Field label="Child's name or nickname">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                placeholder="Mia"
                data-testid="onboard-name"
                required
              />
            </Field>
            <Field label="How old is your little one?">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="number"
                    min="0"
                    max="18"
                    step="1"
                    value={form.age_years_input}
                    onChange={(e) => setField('age_years_input', e.target.value)}
                    className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                    placeholder="Years"
                    data-testid="onboard-age-years"
                  />
                  <p className="text-xs text-stone-400 mt-1 pl-1">Years</p>
                </div>
                <div>
                  <input
                    type="number"
                    min="0"
                    max="11"
                    step="1"
                    value={form.age_months_input}
                    onChange={(e) => setField('age_months_input', e.target.value)}
                    className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                    placeholder="Months"
                    data-testid="onboard-age-months"
                  />
                  <p className="text-xs text-stone-400 mt-1 pl-1">Months (0–11)</p>
                </div>
              </div>
              {(form.age_years_input || form.age_months_input) && (
                <p className="text-sm text-[#7C6F9C] mt-2" data-testid="onboard-age-preview">
                  {form.name || 'Your child'} is {formatAgeReadable(form.age_years_input, form.age_months_input)} old.
                </p>
              )}
              <p className="text-xs text-stone-400 mt-1">
                For a newborn, leave Years blank and use Months (e.g. <em>0 years 2 months</em>).
              </p>
            </Field>
            <Field label="Languages used at home (comma-separated)">
              <input
                type="text"
                value={(form.languages_at_home || []).join(', ')}
                onChange={(e) =>
                  setField(
                    'languages_at_home',
                    e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  )
                }
                className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                placeholder="English, Afrikaans"
                data-testid="onboard-languages"
              />
            </Field>
            <Field label="Country (optional)">
              <input
                type="text"
                value={form.country}
                onChange={(e) => setField('country', e.target.value)}
                className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                placeholder="South Africa"
              />
            </Field>
            <Field label="WhatsApp number (optional — we'll use this for daily ideas later)">
              <input
                type="tel"
                value={form.whatsapp_number}
                onChange={(e) => setField('whatsapp_number', e.target.value)}
                className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                placeholder="+27 82 555 1234"
                data-testid="onboard-whatsapp"
              />
            </Field>
          </Step>
        );
      case 2:
        return (
          <Step title="What brings you to PlayFirst Parent?" subtitle="Pick anything that resonates. Many parents pick more than one.">
            <ChipGrid items={REASONS} selected={form.reasons} onToggle={(v) => toggle('reasons', v)} testidPrefix="onboard-reason" />
          </Step>
        );
      case 3:
        return (
          <Step title="Anything about your child's profile to consider?" subtitle="This is optional. It simply helps us suggest ideas that feel more appropriate for your child.">
            <ChipGrid items={DIAGNOSIS} selected={form.diagnosis_profile} onToggle={(v) => toggle('diagnosis_profile', v)} testidPrefix="onboard-dx" />
          </Step>
        );
      case 4:
        return (
          <Step title="How does your child mostly communicate right now?" subtitle="All communication counts — pick whatever applies.">
            <ChipGrid items={COMMUNICATION_STYLE} selected={form.communication_style} onToggle={(v) => toggle('communication_style', v)} testidPrefix="onboard-comm" />
          </Step>
        );
      case 5:
        return (
          <Step title="What does your child seem to understand?" subtitle="It's normal for children to understand more than they can say.">
            <ChipGrid items={UNDERSTANDING} selected={form.understanding} onToggle={(v) => toggle('understanding', v)} testidPrefix="onboard-und" />
          </Step>
        );
      case 6:
        return (
          <Step title="Repeated phrases, scripts, songs or movie lines?" subtitle="Scripts are real, meaningful communication.">
            <div className="flex flex-wrap gap-2 mb-6">
              {SCRIPT_USE.map((o) => (
                <Chip
                  key={o.v}
                  active={form.uses_scripts === o.v}
                  onClick={() => setField('uses_scripts', o.v)}
                  testid={`onboard-scripts-${o.v}`}
                >
                  {o.l}
                </Chip>
              ))}
            </div>

            {form.uses_scripts && form.uses_scripts !== 'no' && (
              <>
                <p className="text-sm text-stone-600 mb-2">
                  Would you like PlayFirst to use GLP-informed support?
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {GLP_PREFERENCE.map((o) => (
                    <Chip
                      key={o.v}
                      active={form.glp_preference === o.v}
                      onClick={() => setField('glp_preference', o.v)}
                      testid={`onboard-glp-${o.v}`}
                    >
                      {o.l}
                    </Chip>
                  ))}
                </div>

                {form.glp_preference && form.glp_preference !== 'no' && (
                  <>
                    <p className="text-sm text-stone-600 mb-2">Which sounds most like your child?</p>
                    <ChipGrid
                      items={GLP_DESCRIPTOR}
                      selected={form.glp_stage_descriptor ? [form.glp_stage_descriptor] : []}
                      onToggle={(v) =>
                        setField(
                          'glp_stage_descriptor',
                          form.glp_stage_descriptor === v ? '' : v,
                        )
                      }
                      testidPrefix="onboard-glpstage"
                    />
                  </>
                )}
              </>
            )}
          </Step>
        );
      case 7:
        return (
          <Step title="What does your child love right now?" subtitle="Their interests are powerful entry points for connection.">
            <ChipGrid items={INTERESTS} selected={form.interests} onToggle={(v) => toggle('interests', v)} testidPrefix="onboard-int" />

            <p className="text-sm text-stone-600 mt-6 mb-2">How does your child usually play?</p>
            <ChipGrid items={PLAY_STYLE} selected={form.play_style} onToggle={(v) => toggle('play_style', v)} testidPrefix="onboard-play" />
            <p className="text-xs text-stone-500 mt-4">
              Lining up, spinning and repeating aren't problems — they're entry points for
              language and connection. 💛
            </p>
          </Step>
        );
      case 8:
        return (
          <Step title="What helps your child feel ready to communicate?" subtitle="">
            <ChipGrid items={REGULATION_SUPPORTS} selected={form.regulation_supports} onToggle={(v) => toggle('regulation_supports', v)} testidPrefix="onboard-reg" />
            <p className="text-sm text-stone-600 mt-6 mb-2">And what can make communication harder?</p>
            <ChipGrid items={COMMUNICATION_BARRIERS} selected={form.communication_barriers} onToggle={(v) => toggle('communication_barriers', v)} testidPrefix="onboard-bar" />
          </Step>
        );
      case 9:
        return (
          <Step title="What would you most like support with right now?" subtitle="We'll use this to shape your first play idea.">
            <ChipGrid items={PARENT_GOALS} selected={form.parent_goals} onToggle={(v) => toggle('parent_goals', v)} testidPrefix="onboard-goal" />
          </Step>
        );
      case 10:
        return (
          <Step
            title="How often should I check in via WhatsApp?"
            subtitle="I'll send gentle reminders and play ideas — you can change this anytime in Settings."
          >
            <div className="space-y-2">
              {FREQS.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => setField('whatsapp_reminder_frequency', f.v)}
                  data-testid={`onboard-wa-freq-${f.v}`}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                    form.whatsapp_reminder_frequency === f.v
                      ? 'border-[#7C6F9C] bg-[#7C6F9C]/5'
                      : 'border-stone-200 bg-white hover:border-[#7C6F9C]/40'
                  }`}
                >
                  <div className="font-medium text-[#3a2f4f]">{f.l}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{f.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-4">
              We'll only connect WhatsApp once you tap a confirmation link from
              Settings after onboarding — no spam, no surprises. 💛
            </p>
          </Step>
        );
      case 11:
        return (
          <Step title={`Ready to meet ${form.name || 'your child'}'s Language Map?`} subtitle="We'll generate a warm, parent-friendly map and your first play idea right now.">
            <div className={`${PFP.card} p-5 mb-4`}>
              <p className="text-sm text-stone-600 leading-relaxed">
                Your Language Map is a gentle summary of how your child communicates today,
                what helps, and what one helpful next step might be. No scores. No checkboxes.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-sm text-[#7C6F9C]">
              <Sparkles className="w-4 h-4" />
              This usually takes a few seconds.
            </div>
          </Step>
        );
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className={PFP.page + ' flex items-center justify-center'}>
        <p className="text-stone-600">Please sign in first.</p>
      </div>
    );
  }

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 max-w-4xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-xl text-[#3a2f4f]">PlayFirst</span>
          <span className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Feb 2026 — Exit escape hatch. Onboarding was a dead-end
              for dual-role parents whose PFP data lives under a twin
              account: they got bounced here on every visit with no
              way back to the Clinical side. */}
          <button
            type="button"
            onClick={() => {
              const u = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
              const caps = u?.capabilities || {};
              // Dual-role → send to the Clinical side (they can still see
              // 'With my therapist' etc.). Anyone else → landing page.
              if (caps.clinical_parent) {
                navigate('/');
              } else {
                navigate('/parent-app');
              }
            }}
            className="text-xs text-stone-500 hover:text-[#7C6F9C] underline underline-offset-2"
            data-testid="onboard-exit-btn"
          >
            Exit onboarding
          </button>
          <span className="text-xs text-stone-500">
            Step {step} of {TOTAL_STEPS}
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <ProgressBar step={step} />
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <button
            type="button"
            onClick={back}
            disabled={step === 1 || busy}
            className={PFP.btnGhost + ' inline-flex items-center gap-1 disabled:opacity-40'}
            data-testid="onboard-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={next}
            disabled={busy}
            className={PFP.btnPrimary + ' inline-flex items-center gap-2 disabled:opacity-60'}
            data-testid="onboard-next-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {step === TOTAL_STEPS ? (busy ? 'Building…' : 'Build my Language Map') : 'Next'}
            {!busy && step !== TOTAL_STEPS && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </main>
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div>
      <h2 className={`${PFP.h2} mb-2`}>{title}</h2>
      {subtitle && <p className={`${PFP.sub} mb-6`}>{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-stone-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ChipGrid({ items, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const tid = (testidPrefix || 'chip') + '-' + it.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
        return (
          <Chip
            key={it}
            active={(selected || []).includes(it)}
            onClick={() => onToggle(it)}
            testid={tid}
          >
            {it}
          </Chip>
        );
      })}
    </div>
  );
}
